import { BaseAPIRequestFactory, RequiredError } from './baseapi';
import type { Configuration } from '../configuration';
import { RequestContext, HttpMethod, ResponseContext, HttpInfo } from '../http/http';
import { ObjectSerializer } from '../models/ObjectSerializer';
import { ApiException } from './exception';
import { isCodeInRange } from '../util';
import type {SecurityAuthentication} from '../auth/auth';
import type {HttpFile} from '../http/http';


import { AnalyzeSqlQueryRequest } from '../models/AnalyzeSqlQueryRequest';
import { CatalogOperationRequest } from '../models/CatalogOperationRequest';
import { CatalogOperationResponse } from '../models/CatalogOperationResponse';
import { CreateDashboardInput } from '../models/CreateDashboardInput';
import { CreateGraphInput } from '../models/CreateGraphInput';
import { CreateIngestionPipelineInput } from '../models/CreateIngestionPipelineInput';
import { CreateJobTemplateInput } from '../models/CreateJobTemplateInput';
import { CreateNotebookInput } from '../models/CreateNotebookInput';
import { CreateSavedQueryInput } from '../models/CreateSavedQueryInput';
import { CreateSparkEngineInput } from '../models/CreateSparkEngineInput';
import { CreateSqlEngineInput } from '../models/CreateSqlEngineInput';
import { Dashboard } from '../models/Dashboard';
import { DashboardGraph } from '../models/DashboardGraph';
import { DashboardWithGraphs } from '../models/DashboardWithGraphs';
import { ExecuteNotebookCodeInput } from '../models/ExecuteNotebookCodeInput';
import { GetNotebookContent200Response } from '../models/GetNotebookContent200Response';
import { IngestionPipeline } from '../models/IngestionPipeline';
import { JobTemplate } from '../models/JobTemplate';
import { ListDashboardsResponse } from '../models/ListDashboardsResponse';
import { ListIngestionPipelinesResponse } from '../models/ListIngestionPipelinesResponse';
import { ListJobTemplatesResponse } from '../models/ListJobTemplatesResponse';
import { ListNotebooksResponse } from '../models/ListNotebooksResponse';
import { ListSavedQueriesResponse } from '../models/ListSavedQueriesResponse';
import { ListSparkEnginesResponse } from '../models/ListSparkEnginesResponse';
import { ListSparkJobsResponse } from '../models/ListSparkJobsResponse';
import { ListSqlEnginesResponse } from '../models/ListSqlEnginesResponse';
import { ListSqlQueryExecutionsResponse } from '../models/ListSqlQueryExecutionsResponse';
import { Notebook } from '../models/Notebook';
import { NotebookExecutionResult } from '../models/NotebookExecutionResult';
import { NotebookSession } from '../models/NotebookSession';
import { SaveNotebookContentRequest } from '../models/SaveNotebookContentRequest';
import { SavedQuery } from '../models/SavedQuery';
import { SparkEngine } from '../models/SparkEngine';
import { SparkJob } from '../models/SparkJob';
import { SqlEngine } from '../models/SqlEngine';
import { SqlQueryExecution } from '../models/SqlQueryExecution';
import { SqlQueryExecutionResult } from '../models/SqlQueryExecutionResult';
import { StartNotebookSessionInput } from '../models/StartNotebookSessionInput';
import { StartSparkJobInput } from '../models/StartSparkJobInput';
import { StartSqlQueryExecutionInput } from '../models/StartSqlQueryExecutionInput';
import { UpdateDashboardInput } from '../models/UpdateDashboardInput';
import { UpdateGraphInput } from '../models/UpdateGraphInput';
import { UpdateIngestionPipelineInput } from '../models/UpdateIngestionPipelineInput';
import { UpdateJobTemplateInput } from '../models/UpdateJobTemplateInput';
import { UpdateNotebookInput } from '../models/UpdateNotebookInput';
import { UpdateSavedQueryInput } from '../models/UpdateSavedQueryInput';
import { Workspace } from '../models/Workspace';

/**
 * no description
 */
export class LakehouseApiRequestFactory extends BaseAPIRequestFactory {

  /**
   * Analyze SQL query EXPLAIN results with AI
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param analyzeSqlQueryRequest 
   */
  public async analyzeSqlQuery(projectId: string, workspaceId: string, analyzeSqlQueryRequest: AnalyzeSqlQueryRequest, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "analyzeSqlQuery", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "analyzeSqlQuery", "workspaceId");
    }


    // verify required parameter 'analyzeSqlQueryRequest' is not null or undefined
    if (analyzeSqlQueryRequest === null || analyzeSqlQueryRequest === undefined) {
      throw new RequiredError("LakehouseApi", "analyzeSqlQuery", "analyzeSqlQueryRequest");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/sql-query/analyze'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(analyzeSqlQueryRequest, "AnalyzeSqlQueryRequest", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Cancel a Spark job
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sparkJobId The Spark job ID to cancel
   */
  public async cancelSparkJob(projectId: string, workspaceId: string, sparkJobId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "cancelSparkJob", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "cancelSparkJob", "workspaceId");
    }


    // verify required parameter 'sparkJobId' is not null or undefined
    if (sparkJobId === null || sparkJobId === undefined) {
      throw new RequiredError("LakehouseApi", "cancelSparkJob", "sparkJobId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/spark-job/{sparkJobId}/cancel'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'sparkJobId' + '}', encodeURIComponent(String(sparkJobId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Catalog operation envelope (read-only)
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param catalogOperationRequest 
   */
  public async catalogOperation(projectId: string, workspaceId: string, catalogOperationRequest: CatalogOperationRequest, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "catalogOperation", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "catalogOperation", "workspaceId");
    }


    // verify required parameter 'catalogOperationRequest' is not null or undefined
    if (catalogOperationRequest === null || catalogOperationRequest === undefined) {
      throw new RequiredError("LakehouseApi", "catalogOperation", "catalogOperationRequest");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/catalog'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(catalogOperationRequest, "CatalogOperationRequest", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Create a new dashboard
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param createDashboardInput The request body for creating a dashboard
   */
  public async createDashboard(projectId: string, workspaceId: string, createDashboardInput: CreateDashboardInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "createDashboard", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "createDashboard", "workspaceId");
    }


    // verify required parameter 'createDashboardInput' is not null or undefined
    if (createDashboardInput === null || createDashboardInput === undefined) {
      throw new RequiredError("LakehouseApi", "createDashboard", "createDashboardInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/dashboards'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(createDashboardInput, "CreateDashboardInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Create a new graph
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param dashboardId The dashboard ID
   * @param createGraphInput The request body for creating a graph
   */
  public async createGraph(projectId: string, workspaceId: string, dashboardId: string, createGraphInput: CreateGraphInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "createGraph", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "createGraph", "workspaceId");
    }


    // verify required parameter 'dashboardId' is not null or undefined
    if (dashboardId === null || dashboardId === undefined) {
      throw new RequiredError("LakehouseApi", "createGraph", "dashboardId");
    }


    // verify required parameter 'createGraphInput' is not null or undefined
    if (createGraphInput === null || createGraphInput === undefined) {
      throw new RequiredError("LakehouseApi", "createGraph", "createGraphInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/dashboards/{dashboardId}/graphs'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'dashboardId' + '}', encodeURIComponent(String(dashboardId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(createGraphInput, "CreateGraphInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Create a new ingestion pipeline
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param createIngestionPipelineInput The request body for creating an ingestion pipeline
   */
  public async createIngestionPipeline(projectId: string, workspaceId: string, createIngestionPipelineInput: CreateIngestionPipelineInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "createIngestionPipeline", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "createIngestionPipeline", "workspaceId");
    }


    // verify required parameter 'createIngestionPipelineInput' is not null or undefined
    if (createIngestionPipelineInput === null || createIngestionPipelineInput === undefined) {
      throw new RequiredError("LakehouseApi", "createIngestionPipeline", "createIngestionPipelineInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/ingestion-pipeline'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(createIngestionPipelineInput, "CreateIngestionPipelineInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Create a new job template
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param createJobTemplateInput The request body for creating a job template
   */
  public async createJobTemplate(projectId: string, workspaceId: string, createJobTemplateInput: CreateJobTemplateInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "createJobTemplate", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "createJobTemplate", "workspaceId");
    }


    // verify required parameter 'createJobTemplateInput' is not null or undefined
    if (createJobTemplateInput === null || createJobTemplateInput === undefined) {
      throw new RequiredError("LakehouseApi", "createJobTemplate", "createJobTemplateInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/job-template'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(createJobTemplateInput, "CreateJobTemplateInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Create a new notebook
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param createNotebookInput The request body for creating a notebook
   */
  public async createNotebook(projectId: string, workspaceId: string, createNotebookInput: CreateNotebookInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "createNotebook", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "createNotebook", "workspaceId");
    }


    // verify required parameter 'createNotebookInput' is not null or undefined
    if (createNotebookInput === null || createNotebookInput === undefined) {
      throw new RequiredError("LakehouseApi", "createNotebook", "createNotebookInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/notebook'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(createNotebookInput, "CreateNotebookInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Create a new saved query
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param createSavedQueryInput The request body for creating a saved query
   */
  public async createSavedQuery(projectId: string, workspaceId: string, createSavedQueryInput: CreateSavedQueryInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "createSavedQuery", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "createSavedQuery", "workspaceId");
    }


    // verify required parameter 'createSavedQueryInput' is not null or undefined
    if (createSavedQueryInput === null || createSavedQueryInput === undefined) {
      throw new RequiredError("LakehouseApi", "createSavedQuery", "createSavedQueryInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/saved-query'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(createSavedQueryInput, "CreateSavedQueryInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Create a new Spark engine
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param createSparkEngineInput The request body for creating a Spark engine
   */
  public async createSparkEngine(projectId: string, workspaceId: string, createSparkEngineInput: CreateSparkEngineInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "createSparkEngine", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "createSparkEngine", "workspaceId");
    }


    // verify required parameter 'createSparkEngineInput' is not null or undefined
    if (createSparkEngineInput === null || createSparkEngineInput === undefined) {
      throw new RequiredError("LakehouseApi", "createSparkEngine", "createSparkEngineInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/spark-engine'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(createSparkEngineInput, "CreateSparkEngineInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Create a new SQL engine
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param createSqlEngineInput The request body for creating a SQL engine
   */
  public async createSqlEngine(projectId: string, workspaceId: string, createSqlEngineInput: CreateSqlEngineInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "createSqlEngine", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "createSqlEngine", "workspaceId");
    }


    // verify required parameter 'createSqlEngineInput' is not null or undefined
    if (createSqlEngineInput === null || createSqlEngineInput === undefined) {
      throw new RequiredError("LakehouseApi", "createSqlEngine", "createSqlEngineInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/sql-engine'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(createSqlEngineInput, "CreateSqlEngineInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Delete a dashboard
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param dashboardId The dashboard ID
   */
  public async deleteDashboard(projectId: string, workspaceId: string, dashboardId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteDashboard", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteDashboard", "workspaceId");
    }


    // verify required parameter 'dashboardId' is not null or undefined
    if (dashboardId === null || dashboardId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteDashboard", "dashboardId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/dashboards/{dashboardId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'dashboardId' + '}', encodeURIComponent(String(dashboardId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.DELETE, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Delete a graph
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param dashboardId The dashboard ID
   * @param graphId The graph ID
   */
  public async deleteGraph(projectId: string, workspaceId: string, dashboardId: string, graphId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteGraph", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteGraph", "workspaceId");
    }


    // verify required parameter 'dashboardId' is not null or undefined
    if (dashboardId === null || dashboardId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteGraph", "dashboardId");
    }


    // verify required parameter 'graphId' is not null or undefined
    if (graphId === null || graphId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteGraph", "graphId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/dashboards/{dashboardId}/graphs/{graphId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'dashboardId' + '}', encodeURIComponent(String(dashboardId)))
      .replace('{' + 'graphId' + '}', encodeURIComponent(String(graphId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.DELETE, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Delete an ingestion pipeline
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param ingestionPipelineId The ingestion pipeline ID
   */
  public async deleteIngestionPipeline(projectId: string, workspaceId: string, ingestionPipelineId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteIngestionPipeline", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteIngestionPipeline", "workspaceId");
    }


    // verify required parameter 'ingestionPipelineId' is not null or undefined
    if (ingestionPipelineId === null || ingestionPipelineId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteIngestionPipeline", "ingestionPipelineId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/ingestion-pipeline/{ingestionPipelineId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'ingestionPipelineId' + '}', encodeURIComponent(String(ingestionPipelineId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.DELETE, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Delete a job template
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param jobTemplateId The job template ID
   */
  public async deleteJobTemplate(projectId: string, workspaceId: string, jobTemplateId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteJobTemplate", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteJobTemplate", "workspaceId");
    }


    // verify required parameter 'jobTemplateId' is not null or undefined
    if (jobTemplateId === null || jobTemplateId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteJobTemplate", "jobTemplateId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/job-template/{jobTemplateId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'jobTemplateId' + '}', encodeURIComponent(String(jobTemplateId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.DELETE, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Delete a notebook
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param notebookId The notebook ID
   */
  public async deleteNotebook(projectId: string, workspaceId: string, notebookId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteNotebook", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteNotebook", "workspaceId");
    }


    // verify required parameter 'notebookId' is not null or undefined
    if (notebookId === null || notebookId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteNotebook", "notebookId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/notebook/{notebookId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'notebookId' + '}', encodeURIComponent(String(notebookId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.DELETE, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Delete a saved query
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param savedQueryId The saved query ID
   */
  public async deleteSavedQuery(projectId: string, workspaceId: string, savedQueryId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteSavedQuery", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteSavedQuery", "workspaceId");
    }


    // verify required parameter 'savedQueryId' is not null or undefined
    if (savedQueryId === null || savedQueryId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteSavedQuery", "savedQueryId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/saved-query/{savedQueryId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'savedQueryId' + '}', encodeURIComponent(String(savedQueryId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.DELETE, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Delete a Spark engine
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sparkEngineId The Spark engine ID
   */
  public async deleteSparkEngine(projectId: string, workspaceId: string, sparkEngineId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteSparkEngine", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteSparkEngine", "workspaceId");
    }


    // verify required parameter 'sparkEngineId' is not null or undefined
    if (sparkEngineId === null || sparkEngineId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteSparkEngine", "sparkEngineId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/spark-engine/{sparkEngineId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'sparkEngineId' + '}', encodeURIComponent(String(sparkEngineId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.DELETE, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Delete a SQL engine
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sqlEngineId The SQL engine ID
   */
  public async deleteSqlEngine(projectId: string, workspaceId: string, sqlEngineId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteSqlEngine", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteSqlEngine", "workspaceId");
    }


    // verify required parameter 'sqlEngineId' is not null or undefined
    if (sqlEngineId === null || sqlEngineId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteSqlEngine", "sqlEngineId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/sql-engine/{sqlEngineId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'sqlEngineId' + '}', encodeURIComponent(String(sqlEngineId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.DELETE, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Delete a workspace
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   */
  public async deleteWorkspace(projectId: string, workspaceId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteWorkspace", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "deleteWorkspace", "workspaceId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.DELETE, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Execute code in notebook
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param notebookId The notebook ID
   * @param executeNotebookCodeInput Code to execute
   */
  public async executeNotebookCode(projectId: string, workspaceId: string, notebookId: string, executeNotebookCodeInput: ExecuteNotebookCodeInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "executeNotebookCode", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "executeNotebookCode", "workspaceId");
    }


    // verify required parameter 'notebookId' is not null or undefined
    if (notebookId === null || notebookId === undefined) {
      throw new RequiredError("LakehouseApi", "executeNotebookCode", "notebookId");
    }


    // verify required parameter 'executeNotebookCodeInput' is not null or undefined
    if (executeNotebookCodeInput === null || executeNotebookCodeInput === undefined) {
      throw new RequiredError("LakehouseApi", "executeNotebookCode", "executeNotebookCodeInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/notebook/{notebookId}/session/execute'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'notebookId' + '}', encodeURIComponent(String(notebookId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(executeNotebookCodeInput, "ExecuteNotebookCodeInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Explain a Spark job with AI
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sparkJobId The Spark job ID to explain
   */
  public async explainSparkJob(projectId: string, workspaceId: string, sparkJobId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "explainSparkJob", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "explainSparkJob", "workspaceId");
    }


    // verify required parameter 'sparkJobId' is not null or undefined
    if (sparkJobId === null || sparkJobId === undefined) {
      throw new RequiredError("LakehouseApi", "explainSparkJob", "sparkJobId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/spark-job/{sparkJobId}/explain'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'sparkJobId' + '}', encodeURIComponent(String(sparkJobId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get a dashboard
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param dashboardId The dashboard ID
   */
  public async getDashboard(projectId: string, workspaceId: string, dashboardId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getDashboard", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getDashboard", "workspaceId");
    }


    // verify required parameter 'dashboardId' is not null or undefined
    if (dashboardId === null || dashboardId === undefined) {
      throw new RequiredError("LakehouseApi", "getDashboard", "dashboardId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/dashboards/{dashboardId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'dashboardId' + '}', encodeURIComponent(String(dashboardId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get a graph
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param dashboardId The dashboard ID
   * @param graphId The graph ID
   */
  public async getGraph(projectId: string, workspaceId: string, dashboardId: string, graphId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getGraph", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getGraph", "workspaceId");
    }


    // verify required parameter 'dashboardId' is not null or undefined
    if (dashboardId === null || dashboardId === undefined) {
      throw new RequiredError("LakehouseApi", "getGraph", "dashboardId");
    }


    // verify required parameter 'graphId' is not null or undefined
    if (graphId === null || graphId === undefined) {
      throw new RequiredError("LakehouseApi", "getGraph", "graphId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/dashboards/{dashboardId}/graphs/{graphId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'dashboardId' + '}', encodeURIComponent(String(dashboardId)))
      .replace('{' + 'graphId' + '}', encodeURIComponent(String(graphId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get an ingestion pipeline
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param ingestionPipelineId The ingestion pipeline ID
   */
  public async getIngestionPipeline(projectId: string, workspaceId: string, ingestionPipelineId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getIngestionPipeline", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getIngestionPipeline", "workspaceId");
    }


    // verify required parameter 'ingestionPipelineId' is not null or undefined
    if (ingestionPipelineId === null || ingestionPipelineId === undefined) {
      throw new RequiredError("LakehouseApi", "getIngestionPipeline", "ingestionPipelineId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/ingestion-pipeline/{ingestionPipelineId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'ingestionPipelineId' + '}', encodeURIComponent(String(ingestionPipelineId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get a job template
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param jobTemplateId The job template ID
   */
  public async getJobTemplate(projectId: string, workspaceId: string, jobTemplateId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getJobTemplate", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getJobTemplate", "workspaceId");
    }


    // verify required parameter 'jobTemplateId' is not null or undefined
    if (jobTemplateId === null || jobTemplateId === undefined) {
      throw new RequiredError("LakehouseApi", "getJobTemplate", "jobTemplateId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/job-template/{jobTemplateId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'jobTemplateId' + '}', encodeURIComponent(String(jobTemplateId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get a notebook
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param notebookId The notebook ID
   */
  public async getNotebook(projectId: string, workspaceId: string, notebookId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getNotebook", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getNotebook", "workspaceId");
    }


    // verify required parameter 'notebookId' is not null or undefined
    if (notebookId === null || notebookId === undefined) {
      throw new RequiredError("LakehouseApi", "getNotebook", "notebookId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/notebook/{notebookId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'notebookId' + '}', encodeURIComponent(String(notebookId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get notebook content
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param notebookId The notebook ID
   */
  public async getNotebookContent(projectId: string, workspaceId: string, notebookId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getNotebookContent", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getNotebookContent", "workspaceId");
    }


    // verify required parameter 'notebookId' is not null or undefined
    if (notebookId === null || notebookId === undefined) {
      throw new RequiredError("LakehouseApi", "getNotebookContent", "notebookId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/notebook/{notebookId}/content'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'notebookId' + '}', encodeURIComponent(String(notebookId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get execution result
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param notebookId The notebook ID
   * @param executionId The execution ID
   */
  public async getNotebookExecutionResult(projectId: string, workspaceId: string, notebookId: string, executionId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getNotebookExecutionResult", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getNotebookExecutionResult", "workspaceId");
    }


    // verify required parameter 'notebookId' is not null or undefined
    if (notebookId === null || notebookId === undefined) {
      throw new RequiredError("LakehouseApi", "getNotebookExecutionResult", "notebookId");
    }


    // verify required parameter 'executionId' is not null or undefined
    if (executionId === null || executionId === undefined) {
      throw new RequiredError("LakehouseApi", "getNotebookExecutionResult", "executionId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/notebook/{notebookId}/session/executions/{executionId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'notebookId' + '}', encodeURIComponent(String(notebookId)))
      .replace('{' + 'executionId' + '}', encodeURIComponent(String(executionId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get active notebook session
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param notebookId The notebook ID
   */
  public async getNotebookSession(projectId: string, workspaceId: string, notebookId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getNotebookSession", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getNotebookSession", "workspaceId");
    }


    // verify required parameter 'notebookId' is not null or undefined
    if (notebookId === null || notebookId === undefined) {
      throw new RequiredError("LakehouseApi", "getNotebookSession", "notebookId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/notebook/{notebookId}/session'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'notebookId' + '}', encodeURIComponent(String(notebookId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get a saved query
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param savedQueryId The saved query ID
   */
  public async getSavedQuery(projectId: string, workspaceId: string, savedQueryId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getSavedQuery", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getSavedQuery", "workspaceId");
    }


    // verify required parameter 'savedQueryId' is not null or undefined
    if (savedQueryId === null || savedQueryId === undefined) {
      throw new RequiredError("LakehouseApi", "getSavedQuery", "savedQueryId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/saved-query/{savedQueryId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'savedQueryId' + '}', encodeURIComponent(String(savedQueryId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get a Spark engine
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sparkEngineId The Spark engine ID
   */
  public async getSparkEngine(projectId: string, workspaceId: string, sparkEngineId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getSparkEngine", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getSparkEngine", "workspaceId");
    }


    // verify required parameter 'sparkEngineId' is not null or undefined
    if (sparkEngineId === null || sparkEngineId === undefined) {
      throw new RequiredError("LakehouseApi", "getSparkEngine", "sparkEngineId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/spark-engine/{sparkEngineId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'sparkEngineId' + '}', encodeURIComponent(String(sparkEngineId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get a Spark job by ID
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sparkJobId The Spark job ID
   */
  public async getSparkJob(projectId: string, workspaceId: string, sparkJobId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getSparkJob", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getSparkJob", "workspaceId");
    }


    // verify required parameter 'sparkJobId' is not null or undefined
    if (sparkJobId === null || sparkJobId === undefined) {
      throw new RequiredError("LakehouseApi", "getSparkJob", "sparkJobId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/spark-job/{sparkJobId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'sparkJobId' + '}', encodeURIComponent(String(sparkJobId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get a SQL engine
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sqlEngineId The SQL engine ID
   */
  public async getSqlEngine(projectId: string, workspaceId: string, sqlEngineId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getSqlEngine", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getSqlEngine", "workspaceId");
    }


    // verify required parameter 'sqlEngineId' is not null or undefined
    if (sqlEngineId === null || sqlEngineId === undefined) {
      throw new RequiredError("LakehouseApi", "getSqlEngine", "sqlEngineId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/sql-engine/{sqlEngineId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'sqlEngineId' + '}', encodeURIComponent(String(sqlEngineId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get a SQL query execution
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sqlQueryExecutionId The SQL query execution ID
   */
  public async getSqlQueryExecution(projectId: string, workspaceId: string, sqlQueryExecutionId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getSqlQueryExecution", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getSqlQueryExecution", "workspaceId");
    }


    // verify required parameter 'sqlQueryExecutionId' is not null or undefined
    if (sqlQueryExecutionId === null || sqlQueryExecutionId === undefined) {
      throw new RequiredError("LakehouseApi", "getSqlQueryExecution", "sqlQueryExecutionId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/sql-query-execution/{sqlQueryExecutionId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'sqlQueryExecutionId' + '}', encodeURIComponent(String(sqlQueryExecutionId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get SQL query execution result
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sqlQueryExecutionId The SQL query execution ID
   * @param pageToken Token for fetching the next page of results
   * @param pageSize Number of rows per page
   */
  public async getSqlQueryExecutionResult(projectId: string, workspaceId: string, sqlQueryExecutionId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getSqlQueryExecutionResult", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getSqlQueryExecutionResult", "workspaceId");
    }


    // verify required parameter 'sqlQueryExecutionId' is not null or undefined
    if (sqlQueryExecutionId === null || sqlQueryExecutionId === undefined) {
      throw new RequiredError("LakehouseApi", "getSqlQueryExecutionResult", "sqlQueryExecutionId");
    }




    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/sql-query-execution/{sqlQueryExecutionId}/result'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'sqlQueryExecutionId' + '}', encodeURIComponent(String(sqlQueryExecutionId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")

    // Query Params
    if (pageToken !== undefined) {
      requestContext.setQueryParam("pageToken", ObjectSerializer.serialize(pageToken, "string", "tobedefined"));
    }

    // Query Params
    if (pageSize !== undefined) {
      requestContext.setQueryParam("pageSize", ObjectSerializer.serialize(pageSize, "number", ""));
    }


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Get a workspace
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   */
  public async getWorkspace(projectId: string, workspaceId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "getWorkspace", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "getWorkspace", "workspaceId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Interrupt notebook execution
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param notebookId The notebook ID
   */
  public async interruptNotebookExecution(projectId: string, workspaceId: string, notebookId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "interruptNotebookExecution", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "interruptNotebookExecution", "workspaceId");
    }


    // verify required parameter 'notebookId' is not null or undefined
    if (notebookId === null || notebookId === undefined) {
      throw new RequiredError("LakehouseApi", "interruptNotebookExecution", "notebookId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/notebook/{notebookId}/session/interrupt'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'notebookId' + '}', encodeURIComponent(String(notebookId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * List dashboards
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param pageToken Token for pagination
   * @param pageSize Number of results per page
   */
  public async listDashboards(projectId: string, workspaceId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "listDashboards", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "listDashboards", "workspaceId");
    }




    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/dashboards'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")

    // Query Params
    if (pageToken !== undefined) {
      requestContext.setQueryParam("pageToken", ObjectSerializer.serialize(pageToken, "string", "tobedefined"));
    }

    // Query Params
    if (pageSize !== undefined) {
      requestContext.setQueryParam("pageSize", ObjectSerializer.serialize(pageSize, "number", ""));
    }


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * List ingestion pipelines
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   */
  public async listIngestionPipelines(projectId: string, workspaceId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "listIngestionPipelines", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "listIngestionPipelines", "workspaceId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/ingestion-pipeline'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * List job templates
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param pageToken Token for pagination
   * @param pageSize Number of results per page
   */
  public async listJobTemplates(projectId: string, workspaceId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "listJobTemplates", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "listJobTemplates", "workspaceId");
    }




    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/job-template'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")

    // Query Params
    if (pageToken !== undefined) {
      requestContext.setQueryParam("pageToken", ObjectSerializer.serialize(pageToken, "string", "tobedefined"));
    }

    // Query Params
    if (pageSize !== undefined) {
      requestContext.setQueryParam("pageSize", ObjectSerializer.serialize(pageSize, "number", ""));
    }


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * List notebooks
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   */
  public async listNotebooks(projectId: string, workspaceId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "listNotebooks", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "listNotebooks", "workspaceId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/notebook'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * List saved queries
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param tag Filter saved queries by tag
   * @param pageToken Token for pagination
   * @param pageSize Number of results per page
   */
  public async listSavedQueries(projectId: string, workspaceId: string, tag?: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "listSavedQueries", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "listSavedQueries", "workspaceId");
    }





    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/saved-query'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")

    // Query Params
    if (tag !== undefined) {
      requestContext.setQueryParam("tag", ObjectSerializer.serialize(tag, "string", "tobedefined"));
    }

    // Query Params
    if (pageToken !== undefined) {
      requestContext.setQueryParam("pageToken", ObjectSerializer.serialize(pageToken, "string", "tobedefined"));
    }

    // Query Params
    if (pageSize !== undefined) {
      requestContext.setQueryParam("pageSize", ObjectSerializer.serialize(pageSize, "number", ""));
    }


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * List Spark engines
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   */
  public async listSparkEngines(projectId: string, workspaceId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "listSparkEngines", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "listSparkEngines", "workspaceId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/spark-engine'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * List Spark jobs
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sparkEngineIds Filter jobs by Spark engine IDs
   * @param pageToken Token for pagination
   * @param pageSize Maximum number of items to return
   */
  public async listSparkJobs(projectId: string, workspaceId: string, sparkEngineIds?: Array<string>, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "listSparkJobs", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "listSparkJobs", "workspaceId");
    }





    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/spark-job'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")

    // Query Params
    if (sparkEngineIds !== undefined) {
      requestContext.setQueryParam("sparkEngineIds", ObjectSerializer.serialize(sparkEngineIds, "Array<string>", "uuid"));
    }

    // Query Params
    if (pageToken !== undefined) {
      requestContext.setQueryParam("pageToken", ObjectSerializer.serialize(pageToken, "string", "tobedefined"));
    }

    // Query Params
    if (pageSize !== undefined) {
      requestContext.setQueryParam("pageSize", ObjectSerializer.serialize(pageSize, "number", ""));
    }


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * List SQL engines
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   */
  public async listSqlEngines(projectId: string, workspaceId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "listSqlEngines", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "listSqlEngines", "workspaceId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/sql-engine'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * List SQL query executions
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sqlEngineId Filter by SQL engine ID
   * @param startTime Filter by start time (inclusive)
   * @param endTime Filter by end time (inclusive)
   * @param pageToken Token for fetching the next page of results
   * @param pageSize Number of results per page
   */
  public async listSqlQueryExecutions(projectId: string, workspaceId: string, sqlEngineId?: string, startTime?: Date, endTime?: Date, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "listSqlQueryExecutions", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "listSqlQueryExecutions", "workspaceId");
    }







    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/sql-query-execution'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")

    // Query Params
    if (sqlEngineId !== undefined) {
      requestContext.setQueryParam("sqlEngineId", ObjectSerializer.serialize(sqlEngineId, "string", "uuid"));
    }

    // Query Params
    if (startTime !== undefined) {
      requestContext.setQueryParam("startTime", ObjectSerializer.serialize(startTime, "Date", "date-time"));
    }

    // Query Params
    if (endTime !== undefined) {
      requestContext.setQueryParam("endTime", ObjectSerializer.serialize(endTime, "Date", "date-time"));
    }

    // Query Params
    if (pageToken !== undefined) {
      requestContext.setQueryParam("pageToken", ObjectSerializer.serialize(pageToken, "string", "tobedefined"));
    }

    // Query Params
    if (pageSize !== undefined) {
      requestContext.setQueryParam("pageSize", ObjectSerializer.serialize(pageSize, "number", ""));
    }


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * List workspaces
   * @param projectId The project ID
   */
  public async listWorkspaces(projectId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "listWorkspaces", "projectId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.GET, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Save notebook content
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param notebookId The notebook ID
   * @param saveNotebookContentRequest The full .ipynb notebook content
   */
  public async saveNotebookContent(projectId: string, workspaceId: string, notebookId: string, saveNotebookContentRequest: SaveNotebookContentRequest, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "saveNotebookContent", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "saveNotebookContent", "workspaceId");
    }


    // verify required parameter 'notebookId' is not null or undefined
    if (notebookId === null || notebookId === undefined) {
      throw new RequiredError("LakehouseApi", "saveNotebookContent", "notebookId");
    }


    // verify required parameter 'saveNotebookContentRequest' is not null or undefined
    if (saveNotebookContentRequest === null || saveNotebookContentRequest === undefined) {
      throw new RequiredError("LakehouseApi", "saveNotebookContent", "saveNotebookContentRequest");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/notebook/{notebookId}/content'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'notebookId' + '}', encodeURIComponent(String(notebookId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.PUT, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(saveNotebookContentRequest, "SaveNotebookContentRequest", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Start an ingestion pipeline
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param ingestionPipelineId The ingestion pipeline ID
   */
  public async startIngestionPipeline(projectId: string, workspaceId: string, ingestionPipelineId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "startIngestionPipeline", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "startIngestionPipeline", "workspaceId");
    }


    // verify required parameter 'ingestionPipelineId' is not null or undefined
    if (ingestionPipelineId === null || ingestionPipelineId === undefined) {
      throw new RequiredError("LakehouseApi", "startIngestionPipeline", "ingestionPipelineId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/ingestion-pipeline/{ingestionPipelineId}/start'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'ingestionPipelineId' + '}', encodeURIComponent(String(ingestionPipelineId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Start a notebook session
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param notebookId The notebook ID
   * @param startNotebookSessionInput Session configuration
   */
  public async startNotebookSession(projectId: string, workspaceId: string, notebookId: string, startNotebookSessionInput: StartNotebookSessionInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "startNotebookSession", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "startNotebookSession", "workspaceId");
    }


    // verify required parameter 'notebookId' is not null or undefined
    if (notebookId === null || notebookId === undefined) {
      throw new RequiredError("LakehouseApi", "startNotebookSession", "notebookId");
    }


    // verify required parameter 'startNotebookSessionInput' is not null or undefined
    if (startNotebookSessionInput === null || startNotebookSessionInput === undefined) {
      throw new RequiredError("LakehouseApi", "startNotebookSession", "startNotebookSessionInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/notebook/{notebookId}/session'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'notebookId' + '}', encodeURIComponent(String(notebookId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(startNotebookSessionInput, "StartNotebookSessionInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Start a Spark engine
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sparkEngineId The Spark engine ID
   */
  public async startSparkEngine(projectId: string, workspaceId: string, sparkEngineId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "startSparkEngine", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "startSparkEngine", "workspaceId");
    }


    // verify required parameter 'sparkEngineId' is not null or undefined
    if (sparkEngineId === null || sparkEngineId === undefined) {
      throw new RequiredError("LakehouseApi", "startSparkEngine", "sparkEngineId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/spark-engine/{sparkEngineId}/start'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'sparkEngineId' + '}', encodeURIComponent(String(sparkEngineId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Start a new Spark job
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param startSparkJobInput The request body for starting a Spark job
   */
  public async startSparkJob(projectId: string, workspaceId: string, startSparkJobInput: StartSparkJobInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "startSparkJob", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "startSparkJob", "workspaceId");
    }


    // verify required parameter 'startSparkJobInput' is not null or undefined
    if (startSparkJobInput === null || startSparkJobInput === undefined) {
      throw new RequiredError("LakehouseApi", "startSparkJob", "startSparkJobInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/spark-job'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(startSparkJobInput, "StartSparkJobInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Start a SQL engine
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sqlEngineId The SQL engine ID
   */
  public async startSqlEngine(projectId: string, workspaceId: string, sqlEngineId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "startSqlEngine", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "startSqlEngine", "workspaceId");
    }


    // verify required parameter 'sqlEngineId' is not null or undefined
    if (sqlEngineId === null || sqlEngineId === undefined) {
      throw new RequiredError("LakehouseApi", "startSqlEngine", "sqlEngineId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/sql-engine/{sqlEngineId}/start'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'sqlEngineId' + '}', encodeURIComponent(String(sqlEngineId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Start a SQL query execution
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param startSqlQueryExecutionInput The request body for starting a SQL query execution
   */
  public async startSqlQueryExecution(projectId: string, workspaceId: string, startSqlQueryExecutionInput: StartSqlQueryExecutionInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "startSqlQueryExecution", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "startSqlQueryExecution", "workspaceId");
    }


    // verify required parameter 'startSqlQueryExecutionInput' is not null or undefined
    if (startSqlQueryExecutionInput === null || startSqlQueryExecutionInput === undefined) {
      throw new RequiredError("LakehouseApi", "startSqlQueryExecution", "startSqlQueryExecutionInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/sql-query-execution'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(startSqlQueryExecutionInput, "StartSqlQueryExecutionInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Stop an ingestion pipeline
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param ingestionPipelineId The ingestion pipeline ID
   */
  public async stopIngestionPipeline(projectId: string, workspaceId: string, ingestionPipelineId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "stopIngestionPipeline", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "stopIngestionPipeline", "workspaceId");
    }


    // verify required parameter 'ingestionPipelineId' is not null or undefined
    if (ingestionPipelineId === null || ingestionPipelineId === undefined) {
      throw new RequiredError("LakehouseApi", "stopIngestionPipeline", "ingestionPipelineId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/ingestion-pipeline/{ingestionPipelineId}/stop'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'ingestionPipelineId' + '}', encodeURIComponent(String(ingestionPipelineId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Stop a notebook session
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param notebookId The notebook ID
   */
  public async stopNotebookSession(projectId: string, workspaceId: string, notebookId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "stopNotebookSession", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "stopNotebookSession", "workspaceId");
    }


    // verify required parameter 'notebookId' is not null or undefined
    if (notebookId === null || notebookId === undefined) {
      throw new RequiredError("LakehouseApi", "stopNotebookSession", "notebookId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/notebook/{notebookId}/session'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'notebookId' + '}', encodeURIComponent(String(notebookId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.DELETE, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Stop a Spark engine
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sparkEngineId The Spark engine ID
   */
  public async stopSparkEngine(projectId: string, workspaceId: string, sparkEngineId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "stopSparkEngine", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "stopSparkEngine", "workspaceId");
    }


    // verify required parameter 'sparkEngineId' is not null or undefined
    if (sparkEngineId === null || sparkEngineId === undefined) {
      throw new RequiredError("LakehouseApi", "stopSparkEngine", "sparkEngineId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/spark-engine/{sparkEngineId}/stop'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'sparkEngineId' + '}', encodeURIComponent(String(sparkEngineId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Stop a SQL engine
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param sqlEngineId The SQL engine ID
   */
  public async stopSqlEngine(projectId: string, workspaceId: string, sqlEngineId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "stopSqlEngine", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "stopSqlEngine", "workspaceId");
    }


    // verify required parameter 'sqlEngineId' is not null or undefined
    if (sqlEngineId === null || sqlEngineId === undefined) {
      throw new RequiredError("LakehouseApi", "stopSqlEngine", "sqlEngineId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/sql-engine/{sqlEngineId}/stop'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'sqlEngineId' + '}', encodeURIComponent(String(sqlEngineId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Update a dashboard
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param dashboardId The dashboard ID
   * @param updateDashboardInput The request body for updating a dashboard
   */
  public async updateDashboard(projectId: string, workspaceId: string, dashboardId: string, updateDashboardInput: UpdateDashboardInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "updateDashboard", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "updateDashboard", "workspaceId");
    }


    // verify required parameter 'dashboardId' is not null or undefined
    if (dashboardId === null || dashboardId === undefined) {
      throw new RequiredError("LakehouseApi", "updateDashboard", "dashboardId");
    }


    // verify required parameter 'updateDashboardInput' is not null or undefined
    if (updateDashboardInput === null || updateDashboardInput === undefined) {
      throw new RequiredError("LakehouseApi", "updateDashboard", "updateDashboardInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/dashboards/{dashboardId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'dashboardId' + '}', encodeURIComponent(String(dashboardId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.PUT, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(updateDashboardInput, "UpdateDashboardInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Update a graph
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param dashboardId The dashboard ID
   * @param graphId The graph ID
   * @param updateGraphInput The request body for updating a graph
   */
  public async updateGraph(projectId: string, workspaceId: string, dashboardId: string, graphId: string, updateGraphInput: UpdateGraphInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "updateGraph", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "updateGraph", "workspaceId");
    }


    // verify required parameter 'dashboardId' is not null or undefined
    if (dashboardId === null || dashboardId === undefined) {
      throw new RequiredError("LakehouseApi", "updateGraph", "dashboardId");
    }


    // verify required parameter 'graphId' is not null or undefined
    if (graphId === null || graphId === undefined) {
      throw new RequiredError("LakehouseApi", "updateGraph", "graphId");
    }


    // verify required parameter 'updateGraphInput' is not null or undefined
    if (updateGraphInput === null || updateGraphInput === undefined) {
      throw new RequiredError("LakehouseApi", "updateGraph", "updateGraphInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/dashboards/{dashboardId}/graphs/{graphId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'dashboardId' + '}', encodeURIComponent(String(dashboardId)))
      .replace('{' + 'graphId' + '}', encodeURIComponent(String(graphId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.PUT, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(updateGraphInput, "UpdateGraphInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Update an ingestion pipeline
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param ingestionPipelineId The ingestion pipeline ID
   * @param updateIngestionPipelineInput The request body for updating an ingestion pipeline
   */
  public async updateIngestionPipeline(projectId: string, workspaceId: string, ingestionPipelineId: string, updateIngestionPipelineInput: UpdateIngestionPipelineInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "updateIngestionPipeline", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "updateIngestionPipeline", "workspaceId");
    }


    // verify required parameter 'ingestionPipelineId' is not null or undefined
    if (ingestionPipelineId === null || ingestionPipelineId === undefined) {
      throw new RequiredError("LakehouseApi", "updateIngestionPipeline", "ingestionPipelineId");
    }


    // verify required parameter 'updateIngestionPipelineInput' is not null or undefined
    if (updateIngestionPipelineInput === null || updateIngestionPipelineInput === undefined) {
      throw new RequiredError("LakehouseApi", "updateIngestionPipeline", "updateIngestionPipelineInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/ingestion-pipeline/{ingestionPipelineId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'ingestionPipelineId' + '}', encodeURIComponent(String(ingestionPipelineId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.PUT, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(updateIngestionPipelineInput, "UpdateIngestionPipelineInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Update a job template
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param jobTemplateId The job template ID
   * @param updateJobTemplateInput The request body for updating a job template
   */
  public async updateJobTemplate(projectId: string, workspaceId: string, jobTemplateId: string, updateJobTemplateInput: UpdateJobTemplateInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "updateJobTemplate", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "updateJobTemplate", "workspaceId");
    }


    // verify required parameter 'jobTemplateId' is not null or undefined
    if (jobTemplateId === null || jobTemplateId === undefined) {
      throw new RequiredError("LakehouseApi", "updateJobTemplate", "jobTemplateId");
    }


    // verify required parameter 'updateJobTemplateInput' is not null or undefined
    if (updateJobTemplateInput === null || updateJobTemplateInput === undefined) {
      throw new RequiredError("LakehouseApi", "updateJobTemplate", "updateJobTemplateInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/job-template/{jobTemplateId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'jobTemplateId' + '}', encodeURIComponent(String(jobTemplateId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.PUT, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(updateJobTemplateInput, "UpdateJobTemplateInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Update a notebook
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param notebookId The notebook ID
   * @param updateNotebookInput Fields to update
   */
  public async updateNotebook(projectId: string, workspaceId: string, notebookId: string, updateNotebookInput: UpdateNotebookInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "updateNotebook", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "updateNotebook", "workspaceId");
    }


    // verify required parameter 'notebookId' is not null or undefined
    if (notebookId === null || notebookId === undefined) {
      throw new RequiredError("LakehouseApi", "updateNotebook", "notebookId");
    }


    // verify required parameter 'updateNotebookInput' is not null or undefined
    if (updateNotebookInput === null || updateNotebookInput === undefined) {
      throw new RequiredError("LakehouseApi", "updateNotebook", "updateNotebookInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/notebook/{notebookId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'notebookId' + '}', encodeURIComponent(String(notebookId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.PATCH, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(updateNotebookInput, "UpdateNotebookInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Update a saved query
   * @param projectId The project ID
   * @param workspaceId The workspace ID
   * @param savedQueryId The saved query ID
   * @param updateSavedQueryInput The request body for updating a saved query
   */
  public async updateSavedQuery(projectId: string, workspaceId: string, savedQueryId: string, updateSavedQueryInput: UpdateSavedQueryInput, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LakehouseApi", "updateSavedQuery", "projectId");
    }


    // verify required parameter 'workspaceId' is not null or undefined
    if (workspaceId === null || workspaceId === undefined) {
      throw new RequiredError("LakehouseApi", "updateSavedQuery", "workspaceId");
    }


    // verify required parameter 'savedQueryId' is not null or undefined
    if (savedQueryId === null || savedQueryId === undefined) {
      throw new RequiredError("LakehouseApi", "updateSavedQuery", "savedQueryId");
    }


    // verify required parameter 'updateSavedQueryInput' is not null or undefined
    if (updateSavedQueryInput === null || updateSavedQueryInput === undefined) {
      throw new RequiredError("LakehouseApi", "updateSavedQuery", "updateSavedQueryInput");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/lakehouse/workspace/{workspaceId}/saved-query/{savedQueryId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'workspaceId' + '}', encodeURIComponent(String(workspaceId)))
      .replace('{' + 'savedQueryId' + '}', encodeURIComponent(String(savedQueryId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.PUT, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(updateSavedQueryInput, "UpdateSavedQueryInput", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

}

export class LakehouseApiResponseProcessor {

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to analyzeSqlQuery
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async analyzeSqlQueryWithHttpInfo(response: ResponseContext): Promise<HttpInfo<string >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: string = ObjectSerializer.deserialize(
        await response.body.json(),
        "string", ""
      ) as string;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Query execution is not yet complete", undefined, response.headers, response);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "SQL query execution not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: string = await response.body.json() as string;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to cancelSparkJob
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async cancelSparkJobWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SparkJob >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SparkJob = ObjectSerializer.deserialize(
        await response.body.json(),
        "SparkJob", ""
      ) as SparkJob;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Spark job not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SparkJob = await response.body.json() as SparkJob;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to catalogOperation
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async catalogOperationWithHttpInfo(response: ResponseContext): Promise<HttpInfo<CatalogOperationResponse >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: CatalogOperationResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "CatalogOperationResponse", ""
      ) as CatalogOperationResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Malformed envelope (bad method, missing path)", undefined, response.headers, response);
    }
    if (isCodeInRange("502", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Catalog service unreachable", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: CatalogOperationResponse = await response.body.json() as CatalogOperationResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to createDashboard
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async createDashboardWithHttpInfo(response: ResponseContext): Promise<HttpInfo<Dashboard >> {
    if (isCodeInRange("201", response.httpStatusCode)) {
      const body: Dashboard = ObjectSerializer.deserialize(
        await response.body.json(),
        "Dashboard", ""
      ) as Dashboard;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("409", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Dashboard with this name already exists", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: Dashboard = await response.body.json() as Dashboard;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to createGraph
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async createGraphWithHttpInfo(response: ResponseContext): Promise<HttpInfo<DashboardGraph >> {
    if (isCodeInRange("201", response.httpStatusCode)) {
      const body: DashboardGraph = ObjectSerializer.deserialize(
        await response.body.json(),
        "DashboardGraph", ""
      ) as DashboardGraph;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Dashboard not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: DashboardGraph = await response.body.json() as DashboardGraph;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to createIngestionPipeline
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async createIngestionPipelineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<IngestionPipeline >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: IngestionPipeline = ObjectSerializer.deserialize(
        await response.body.json(),
        "IngestionPipeline", ""
      ) as IngestionPipeline;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("409", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Ingestion pipeline already exists", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: IngestionPipeline = await response.body.json() as IngestionPipeline;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to createJobTemplate
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async createJobTemplateWithHttpInfo(response: ResponseContext): Promise<HttpInfo<JobTemplate >> {
    if (isCodeInRange("201", response.httpStatusCode)) {
      const body: JobTemplate = ObjectSerializer.deserialize(
        await response.body.json(),
        "JobTemplate", ""
      ) as JobTemplate;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("409", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Job template already exists", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: JobTemplate = await response.body.json() as JobTemplate;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to createNotebook
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async createNotebookWithHttpInfo(response: ResponseContext): Promise<HttpInfo<Notebook >> {
    if (isCodeInRange("201", response.httpStatusCode)) {
      const body: Notebook = ObjectSerializer.deserialize(
        await response.body.json(),
        "Notebook", ""
      ) as Notebook;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("409", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Notebook with same name already exists", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: Notebook = await response.body.json() as Notebook;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to createSavedQuery
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async createSavedQueryWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SavedQuery >> {
    if (isCodeInRange("201", response.httpStatusCode)) {
      const body: SavedQuery = ObjectSerializer.deserialize(
        await response.body.json(),
        "SavedQuery", ""
      ) as SavedQuery;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("409", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Saved query with this name already exists", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SavedQuery = await response.body.json() as SavedQuery;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to createSparkEngine
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async createSparkEngineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SparkEngine >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SparkEngine = ObjectSerializer.deserialize(
        await response.body.json(),
        "SparkEngine", ""
      ) as SparkEngine;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("409", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Spark engine already exists", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SparkEngine = await response.body.json() as SparkEngine;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to createSqlEngine
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async createSqlEngineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SqlEngine >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SqlEngine = ObjectSerializer.deserialize(
        await response.body.json(),
        "SqlEngine", ""
      ) as SqlEngine;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("409", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "SQL engine already exists", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SqlEngine = await response.body.json() as SqlEngine;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to deleteDashboard
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async deleteDashboardWithHttpInfo(response: ResponseContext): Promise<HttpInfo<void >> {
    if (isCodeInRange("204", response.httpStatusCode)) {
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, undefined);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Dashboard not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: void = await response.body.json() as void;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to deleteGraph
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async deleteGraphWithHttpInfo(response: ResponseContext): Promise<HttpInfo<void >> {
    if (isCodeInRange("204", response.httpStatusCode)) {
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, undefined);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Graph not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: void = await response.body.json() as void;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to deleteIngestionPipeline
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async deleteIngestionPipelineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<IngestionPipeline >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: IngestionPipeline = ObjectSerializer.deserialize(
        await response.body.json(),
        "IngestionPipeline", ""
      ) as IngestionPipeline;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Ingestion pipeline not found", undefined, response.headers, response);
    }
    if (isCodeInRange("409", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Operation already in progress", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: IngestionPipeline = await response.body.json() as IngestionPipeline;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to deleteJobTemplate
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async deleteJobTemplateWithHttpInfo(response: ResponseContext): Promise<HttpInfo<void >> {
    if (isCodeInRange("204", response.httpStatusCode)) {
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, undefined);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Job template not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: void = await response.body.json() as void;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to deleteNotebook
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async deleteNotebookWithHttpInfo(response: ResponseContext): Promise<HttpInfo<void >> {
    if (isCodeInRange("204", response.httpStatusCode)) {
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, undefined);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Notebook not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: void = await response.body.json() as void;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to deleteSavedQuery
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async deleteSavedQueryWithHttpInfo(response: ResponseContext): Promise<HttpInfo<void >> {
    if (isCodeInRange("204", response.httpStatusCode)) {
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, undefined);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Saved query not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: void = await response.body.json() as void;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to deleteSparkEngine
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async deleteSparkEngineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<void >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, undefined);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Spark engine not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: void = await response.body.json() as void;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to deleteSqlEngine
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async deleteSqlEngineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<void >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, undefined);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "SQL engine not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: void = await response.body.json() as void;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to deleteWorkspace
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async deleteWorkspaceWithHttpInfo(response: ResponseContext): Promise<HttpInfo<void >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, undefined);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Workspace not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: void = await response.body.json() as void;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to executeNotebookCode
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async executeNotebookCodeWithHttpInfo(response: ResponseContext): Promise<HttpInfo<NotebookExecutionResult >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: NotebookExecutionResult = ObjectSerializer.deserialize(
        await response.body.json(),
        "NotebookExecutionResult", ""
      ) as NotebookExecutionResult;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "No active session", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: NotebookExecutionResult = await response.body.json() as NotebookExecutionResult;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to explainSparkJob
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async explainSparkJobWithHttpInfo(response: ResponseContext): Promise<HttpInfo<string >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: string = ObjectSerializer.deserialize(
        await response.body.json(),
        "string", ""
      ) as string;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Job is not in a terminal status or has no Spark application ID", undefined, response.headers, response);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Spark job not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: string = await response.body.json() as string;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getDashboard
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getDashboardWithHttpInfo(response: ResponseContext): Promise<HttpInfo<DashboardWithGraphs >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: DashboardWithGraphs = ObjectSerializer.deserialize(
        await response.body.json(),
        "DashboardWithGraphs", ""
      ) as DashboardWithGraphs;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Dashboard not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: DashboardWithGraphs = await response.body.json() as DashboardWithGraphs;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getGraph
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getGraphWithHttpInfo(response: ResponseContext): Promise<HttpInfo<DashboardGraph >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: DashboardGraph = ObjectSerializer.deserialize(
        await response.body.json(),
        "DashboardGraph", ""
      ) as DashboardGraph;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Graph not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: DashboardGraph = await response.body.json() as DashboardGraph;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getIngestionPipeline
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getIngestionPipelineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<IngestionPipeline >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: IngestionPipeline = ObjectSerializer.deserialize(
        await response.body.json(),
        "IngestionPipeline", ""
      ) as IngestionPipeline;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Ingestion pipeline not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: IngestionPipeline = await response.body.json() as IngestionPipeline;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getJobTemplate
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getJobTemplateWithHttpInfo(response: ResponseContext): Promise<HttpInfo<JobTemplate >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: JobTemplate = ObjectSerializer.deserialize(
        await response.body.json(),
        "JobTemplate", ""
      ) as JobTemplate;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Job template not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: JobTemplate = await response.body.json() as JobTemplate;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getNotebook
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getNotebookWithHttpInfo(response: ResponseContext): Promise<HttpInfo<Notebook >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: Notebook = ObjectSerializer.deserialize(
        await response.body.json(),
        "Notebook", ""
      ) as Notebook;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Notebook not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: Notebook = await response.body.json() as Notebook;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getNotebookContent
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getNotebookContentWithHttpInfo(response: ResponseContext): Promise<HttpInfo<GetNotebookContent200Response >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: GetNotebookContent200Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "GetNotebookContent200Response", ""
      ) as GetNotebookContent200Response;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Notebook not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: GetNotebookContent200Response = await response.body.json() as GetNotebookContent200Response;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getNotebookExecutionResult
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getNotebookExecutionResultWithHttpInfo(response: ResponseContext): Promise<HttpInfo<NotebookExecutionResult >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: NotebookExecutionResult = ObjectSerializer.deserialize(
        await response.body.json(),
        "NotebookExecutionResult", ""
      ) as NotebookExecutionResult;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: NotebookExecutionResult = await response.body.json() as NotebookExecutionResult;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getNotebookSession
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getNotebookSessionWithHttpInfo(response: ResponseContext): Promise<HttpInfo<NotebookSession >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: NotebookSession = ObjectSerializer.deserialize(
        await response.body.json(),
        "NotebookSession", ""
      ) as NotebookSession;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "No active session", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: NotebookSession = await response.body.json() as NotebookSession;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getSavedQuery
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getSavedQueryWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SavedQuery >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SavedQuery = ObjectSerializer.deserialize(
        await response.body.json(),
        "SavedQuery", ""
      ) as SavedQuery;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Saved query not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SavedQuery = await response.body.json() as SavedQuery;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getSparkEngine
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getSparkEngineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SparkEngine >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SparkEngine = ObjectSerializer.deserialize(
        await response.body.json(),
        "SparkEngine", ""
      ) as SparkEngine;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Spark engine not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SparkEngine = await response.body.json() as SparkEngine;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getSparkJob
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getSparkJobWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SparkJob >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SparkJob = ObjectSerializer.deserialize(
        await response.body.json(),
        "SparkJob", ""
      ) as SparkJob;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Spark job not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SparkJob = await response.body.json() as SparkJob;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getSqlEngine
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getSqlEngineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SqlEngine >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SqlEngine = ObjectSerializer.deserialize(
        await response.body.json(),
        "SqlEngine", ""
      ) as SqlEngine;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "SQL engine not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SqlEngine = await response.body.json() as SqlEngine;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getSqlQueryExecution
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getSqlQueryExecutionWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SqlQueryExecution >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SqlQueryExecution = ObjectSerializer.deserialize(
        await response.body.json(),
        "SqlQueryExecution", ""
      ) as SqlQueryExecution;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "SQL query execution not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SqlQueryExecution = await response.body.json() as SqlQueryExecution;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getSqlQueryExecutionResult
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getSqlQueryExecutionResultWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SqlQueryExecutionResult >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SqlQueryExecutionResult = ObjectSerializer.deserialize(
        await response.body.json(),
        "SqlQueryExecutionResult", ""
      ) as SqlQueryExecutionResult;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Query execution has not completed or failed", undefined, response.headers, response);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "SQL query execution not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SqlQueryExecutionResult = await response.body.json() as SqlQueryExecutionResult;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getWorkspace
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getWorkspaceWithHttpInfo(response: ResponseContext): Promise<HttpInfo<Workspace >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: Workspace = ObjectSerializer.deserialize(
        await response.body.json(),
        "Workspace", ""
      ) as Workspace;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Workspace not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: Workspace = await response.body.json() as Workspace;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to interruptNotebookExecution
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async interruptNotebookExecutionWithHttpInfo(response: ResponseContext): Promise<HttpInfo<void >> {
    if (isCodeInRange("204", response.httpStatusCode)) {
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, undefined);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "No active session", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: void = await response.body.json() as void;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to listDashboards
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async listDashboardsWithHttpInfo(response: ResponseContext): Promise<HttpInfo<ListDashboardsResponse >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: ListDashboardsResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "ListDashboardsResponse", ""
      ) as ListDashboardsResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: ListDashboardsResponse = await response.body.json() as ListDashboardsResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to listIngestionPipelines
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async listIngestionPipelinesWithHttpInfo(response: ResponseContext): Promise<HttpInfo<ListIngestionPipelinesResponse >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: ListIngestionPipelinesResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "ListIngestionPipelinesResponse", ""
      ) as ListIngestionPipelinesResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: ListIngestionPipelinesResponse = await response.body.json() as ListIngestionPipelinesResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to listJobTemplates
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async listJobTemplatesWithHttpInfo(response: ResponseContext): Promise<HttpInfo<ListJobTemplatesResponse >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: ListJobTemplatesResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "ListJobTemplatesResponse", ""
      ) as ListJobTemplatesResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: ListJobTemplatesResponse = await response.body.json() as ListJobTemplatesResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to listNotebooks
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async listNotebooksWithHttpInfo(response: ResponseContext): Promise<HttpInfo<ListNotebooksResponse >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: ListNotebooksResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "ListNotebooksResponse", ""
      ) as ListNotebooksResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: ListNotebooksResponse = await response.body.json() as ListNotebooksResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to listSavedQueries
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async listSavedQueriesWithHttpInfo(response: ResponseContext): Promise<HttpInfo<ListSavedQueriesResponse >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: ListSavedQueriesResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "ListSavedQueriesResponse", ""
      ) as ListSavedQueriesResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: ListSavedQueriesResponse = await response.body.json() as ListSavedQueriesResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to listSparkEngines
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async listSparkEnginesWithHttpInfo(response: ResponseContext): Promise<HttpInfo<ListSparkEnginesResponse >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: ListSparkEnginesResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "ListSparkEnginesResponse", ""
      ) as ListSparkEnginesResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: ListSparkEnginesResponse = await response.body.json() as ListSparkEnginesResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to listSparkJobs
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async listSparkJobsWithHttpInfo(response: ResponseContext): Promise<HttpInfo<ListSparkJobsResponse >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: ListSparkJobsResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "ListSparkJobsResponse", ""
      ) as ListSparkJobsResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: ListSparkJobsResponse = await response.body.json() as ListSparkJobsResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to listSqlEngines
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async listSqlEnginesWithHttpInfo(response: ResponseContext): Promise<HttpInfo<ListSqlEnginesResponse >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: ListSqlEnginesResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "ListSqlEnginesResponse", ""
      ) as ListSqlEnginesResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: ListSqlEnginesResponse = await response.body.json() as ListSqlEnginesResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to listSqlQueryExecutions
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async listSqlQueryExecutionsWithHttpInfo(response: ResponseContext): Promise<HttpInfo<ListSqlQueryExecutionsResponse >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: ListSqlQueryExecutionsResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "ListSqlQueryExecutionsResponse", ""
      ) as ListSqlQueryExecutionsResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: ListSqlQueryExecutionsResponse = await response.body.json() as ListSqlQueryExecutionsResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to listWorkspaces
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async listWorkspacesWithHttpInfo(response: ResponseContext): Promise<HttpInfo<Array<Workspace> >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: Array<Workspace> = ObjectSerializer.deserialize(
        await response.body.json(),
        "Array<Workspace>", ""
      ) as Array<Workspace>;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: Array<Workspace> = await response.body.json() as Array<Workspace>;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to saveNotebookContent
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async saveNotebookContentWithHttpInfo(response: ResponseContext): Promise<HttpInfo<void >> {
    if (isCodeInRange("204", response.httpStatusCode)) {
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, undefined);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Notebook not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: void = await response.body.json() as void;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to startIngestionPipeline
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async startIngestionPipelineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<IngestionPipeline >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: IngestionPipeline = ObjectSerializer.deserialize(
        await response.body.json(),
        "IngestionPipeline", ""
      ) as IngestionPipeline;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Ingestion pipeline not found", undefined, response.headers, response);
    }
    if (isCodeInRange("409", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Operation already in progress", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: IngestionPipeline = await response.body.json() as IngestionPipeline;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to startNotebookSession
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async startNotebookSessionWithHttpInfo(response: ResponseContext): Promise<HttpInfo<NotebookSession >> {
    if (isCodeInRange("201", response.httpStatusCode)) {
      const body: NotebookSession = ObjectSerializer.deserialize(
        await response.body.json(),
        "NotebookSession", ""
      ) as NotebookSession;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Engine not started or session already active", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: NotebookSession = await response.body.json() as NotebookSession;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to startSparkEngine
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async startSparkEngineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SparkEngine >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SparkEngine = ObjectSerializer.deserialize(
        await response.body.json(),
        "SparkEngine", ""
      ) as SparkEngine;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Spark engine not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SparkEngine = await response.body.json() as SparkEngine;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to startSparkJob
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async startSparkJobWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SparkJob >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SparkJob = ObjectSerializer.deserialize(
        await response.body.json(),
        "SparkJob", ""
      ) as SparkJob;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Spark engine not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SparkJob = await response.body.json() as SparkJob;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to startSqlEngine
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async startSqlEngineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SqlEngine >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SqlEngine = ObjectSerializer.deserialize(
        await response.body.json(),
        "SqlEngine", ""
      ) as SqlEngine;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "SQL engine not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SqlEngine = await response.body.json() as SqlEngine;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to startSqlQueryExecution
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async startSqlQueryExecutionWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SqlQueryExecution >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SqlQueryExecution = ObjectSerializer.deserialize(
        await response.body.json(),
        "SqlQueryExecution", ""
      ) as SqlQueryExecution;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Invalid request", undefined, response.headers, response);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "SQL engine not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SqlQueryExecution = await response.body.json() as SqlQueryExecution;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to stopIngestionPipeline
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async stopIngestionPipelineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<IngestionPipeline >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: IngestionPipeline = ObjectSerializer.deserialize(
        await response.body.json(),
        "IngestionPipeline", ""
      ) as IngestionPipeline;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Ingestion pipeline not found", undefined, response.headers, response);
    }
    if (isCodeInRange("409", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Operation already in progress", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: IngestionPipeline = await response.body.json() as IngestionPipeline;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to stopNotebookSession
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async stopNotebookSessionWithHttpInfo(response: ResponseContext): Promise<HttpInfo<NotebookSession >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: NotebookSession = ObjectSerializer.deserialize(
        await response.body.json(),
        "NotebookSession", ""
      ) as NotebookSession;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "No active session", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: NotebookSession = await response.body.json() as NotebookSession;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to stopSparkEngine
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async stopSparkEngineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SparkEngine >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SparkEngine = ObjectSerializer.deserialize(
        await response.body.json(),
        "SparkEngine", ""
      ) as SparkEngine;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Spark engine not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SparkEngine = await response.body.json() as SparkEngine;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to stopSqlEngine
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async stopSqlEngineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SqlEngine >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SqlEngine = ObjectSerializer.deserialize(
        await response.body.json(),
        "SqlEngine", ""
      ) as SqlEngine;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "SQL engine not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SqlEngine = await response.body.json() as SqlEngine;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to updateDashboard
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async updateDashboardWithHttpInfo(response: ResponseContext): Promise<HttpInfo<Dashboard >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: Dashboard = ObjectSerializer.deserialize(
        await response.body.json(),
        "Dashboard", ""
      ) as Dashboard;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Dashboard not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: Dashboard = await response.body.json() as Dashboard;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to updateGraph
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async updateGraphWithHttpInfo(response: ResponseContext): Promise<HttpInfo<DashboardGraph >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: DashboardGraph = ObjectSerializer.deserialize(
        await response.body.json(),
        "DashboardGraph", ""
      ) as DashboardGraph;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Graph not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: DashboardGraph = await response.body.json() as DashboardGraph;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to updateIngestionPipeline
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async updateIngestionPipelineWithHttpInfo(response: ResponseContext): Promise<HttpInfo<IngestionPipeline >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: IngestionPipeline = ObjectSerializer.deserialize(
        await response.body.json(),
        "IngestionPipeline", ""
      ) as IngestionPipeline;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Invalid request — missing required source properties or validation failure", undefined, response.headers, response);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Ingestion pipeline not found", undefined, response.headers, response);
    }
    if (isCodeInRange("409", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Operation in progress or invalid state for update", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: IngestionPipeline = await response.body.json() as IngestionPipeline;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to updateJobTemplate
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async updateJobTemplateWithHttpInfo(response: ResponseContext): Promise<HttpInfo<JobTemplate >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: JobTemplate = ObjectSerializer.deserialize(
        await response.body.json(),
        "JobTemplate", ""
      ) as JobTemplate;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Job template not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: JobTemplate = await response.body.json() as JobTemplate;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to updateNotebook
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async updateNotebookWithHttpInfo(response: ResponseContext): Promise<HttpInfo<Notebook >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: Notebook = ObjectSerializer.deserialize(
        await response.body.json(),
        "Notebook", ""
      ) as Notebook;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Notebook not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: Notebook = await response.body.json() as Notebook;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to updateSavedQuery
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async updateSavedQueryWithHttpInfo(response: ResponseContext): Promise<HttpInfo<SavedQuery >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: SavedQuery = ObjectSerializer.deserialize(
        await response.body.json(),
        "SavedQuery", ""
      ) as SavedQuery;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Saved query not found", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: SavedQuery = await response.body.json() as SavedQuery;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

}
